import { useContext, useLayoutEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { setActiveScope } from '@trinity/chat/api';
import {
  planScopeApplication,
  resetScopedQueries,
  SCOPE_INDEPENDENT_QUERY_KEYS,
} from '@trinity/chat-components/containers/ActiveScope/active-scope-cache.js';
import { useTelemetryLogger } from '@trinity/observability-web/react';
import { useActiveApplicationIdentifier } from '@trinity/chat-components/containers/UserApplications/useActiveApplicationIdentifier';
import { UserApplicationsContext } from '@trinity/chat-components/containers/UserApplications/UserApplicationsContext';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';

type Props = {
  /** Extra first query-key elements to keep across a scope change, on top of `SCOPE_INDEPENDENT_QUERY_KEYS`. */
  extraScopeIndependentQueryKeys?: readonly string[];
  /**
   * Whether the application must be one of the active brand's applications (from
   * `UserApplicationsContext`) before the scope is applied. Pass `false` where that context is not
   * provided (it then stays empty) and the brand and application always change together.
   */
  verifyApplicationMembership?: boolean;
};

/**
 * Mirrors the active brand and application into the module-level scope that the API client's
 * request interceptor reads (axios interceptors cannot use hooks), and resets the query cache when
 * that scope changes. Render it inside the QueryClientProvider and inside both the active brand and
 * active application providers, as a sibling of (or above) anything that makes scoped requests.
 *
 * Contract: scoped requests carry the scope in headers, so their query keys do not need to contain
 * the brand or application. Instead, every query whose first key element is not listed in
 * `SCOPE_INDEPENDENT_QUERY_KEYS` (or `extraScopeIndependentQueryKeys`) is cancelled and reset when
 * the scope changes. Anything that must survive a scope change has to be listed there. A key may
 * still contain the brand/application when the request takes it as a real parameter.
 *
 * Only a pair where both values are set and the application belongs to the brand is applied. After a
 * brand switch the application follows later, so (new brand, old application) is skipped: the scope
 * is cleared and the cache is left alone until the matching pair arrives. The first scope never
 * resets the cache, nor does re-applying the same scope (e.g. strict-mode double effects). The
 * previous scope is kept in a ref, so a remount of this component counts as a first scope.
 *
 * A layout effect is used on purpose: layout effects of a commit run before any passive effect, so
 * the scope is in place before descendants' first requests (react-query fetches start in passive
 * effects), and the holder is updated before the cache reset triggers refetches. The scope is
 * cleared when the pair is not applicable and on unmount, so a request is never sent with half a
 * scope or a stale one.
 */
export const ActiveScopeSync = ({
  extraScopeIndependentQueryKeys,
  verifyApplicationMembership = true,
}: Props): null => {
  const brandIdentifier = useActiveBrandIdentifier();
  const applicationIdentifier = useActiveApplicationIdentifier();
  const userApplications = useContext(UserApplicationsContext);
  const queryClient = useQueryClient();
  const logger = useTelemetryLogger();
  const appliedScopeKeyRef = useRef<string | null>(null);

  const scopeIndependentKeys = useMemo<readonly string[]>(
    () =>
      extraScopeIndependentQueryKeys?.length
        ? [...SCOPE_INDEPENDENT_QUERY_KEYS, ...extraScopeIndependentQueryKeys]
        : SCOPE_INDEPENDENT_QUERY_KEYS,
    [extraScopeIndependentQueryKeys]
  );

  useLayoutEffect(() => {
    const plan = planScopeApplication({
      brandIdentifier,
      applicationIdentifier,
      applicationsByBrand: verifyApplicationMembership ? userApplications : null,
      previousScopeKey: appliedScopeKeyRef.current,
    });

    if (!plan.applicable) {
      setActiveScope(null);
      return undefined;
    }

    // Holder first, synchronously: refetches started by the reset below must pick up the new headers.
    setActiveScope(plan.scope);
    appliedScopeKeyRef.current = plan.scopeKey;

    if (plan.resetCache) {
      resetScopedQueries(queryClient, scopeIndependentKeys).catch((error) => {
        logger.captureException(error, { tags: { area: 'active-scope-cache-reset' } });
      });
    }

    return () => {
      setActiveScope(null);
    };
  }, [
    brandIdentifier,
    applicationIdentifier,
    userApplications,
    verifyApplicationMembership,
    queryClient,
    scopeIndependentKeys,
    logger,
  ]);

  return null;
};
